import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../socket/SocketProvider";
import { appointmentCancel } from "../../service/videoCallService";
import { toast } from "react-toastify";
import { forceAcquireCallLock } from "../../util/sessionTabLock";
import { isInstantAppointment } from "../../util/timezoneHelpers";
import { resolveConsultationPath } from "../../util/callPathHelpers";

import moment from "moment-timezone";

export const JoinChatConfirmModal = ({ isOpen, setIsOpen, callerData, appointmentData, callId }) => {
  const navigate = useNavigate();
  const socket = useSocket();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const [callData, setCallData] = useState(null);

  useEffect(() => {
    if (callerData) {
      setCallData(callerData);
    }
  }, [callerData]);

  useSocket("chatSessionIndirectResponse", (data) => {
    if (isOpen) {
      setCallData(data);
    }
  }, [isOpen]);

  useSocket("chatJoinConfirmResponse", (data) => {
    if (!isOpen) return;

    const apptId = String(data?.appointmentId || "");
    const clientId = String(
      data?.clientId ||
      data?.data?.clientId ||
      data?.callerData?.user?.id ||
      data?.user?.id ||
      "",
    );
    const myId = String(userDetails?._id || "");
    const isClient = myId && clientId === myId;

    // Also accept if appointment matches what we booked (2nd call id match)
    const matchesBooked =
      !appointmentData?.appointmentId ||
      String(appointmentData.appointmentId) === apptId;

    if ((isClient || matchesBooked) && matchesBooked) {
      try {
        const prev = JSON.parse(localStorage.getItem("appointmentData") || "{}");
        const startIso =
          data?.appointmentStartTime ||
          data?.sessionStartTime ||
          appointmentData?.appointmentStartTime ||
          new Date().toISOString();
        localStorage.setItem(
          "appointmentData",
          JSON.stringify({
            ...prev,
            ...(appointmentData || {}),
            appointmentId: apptId,
            appointmentStartTime: startIso,
            sessionStartTime: prev?.sessionStartTime || startIso,
            details: appointmentData?.details || prev?.details || data?.details,
            appointmentEndTime:
              appointmentData?.details?.appointmentEndTime ||
              appointmentData?.appointmentEndTime ||
              prev?.details?.appointmentEndTime ||
              prev?.appointmentEndTime ||
              data?.appointmentEndTime,
            mode: "chat",
            clientId: clientId || prev.clientId,
            consultantId:
              data?.consultantId ||
              appointmentData?.consultantId ||
              prev.consultantId,
          }),
        );
        localStorage.setItem("joined", "true");
        sessionStorage.removeItem(`prontopsy_rejoin_wait_${apptId}`);
        if (!localStorage.getItem(`actualConnectedSeconds_${apptId}`)) {
          localStorage.setItem(`actualConnectedSeconds_${apptId}`, "0");
        }
        localStorage.removeItem("actualConnectedSeconds");
      } catch (_) {
        localStorage.setItem("joined", "true");
      }
      setIsOpen(false);
      // New call entry — clear ghost locks from previous multi-tab paste
      forceAcquireCallLock(apptId);
      navigate(resolveConsultationPath(apptId, "chat"), { replace: true });
    }
  }, [userDetails, navigate, appointmentData, isOpen]);

  const handleJoinLater = () => {
    setIsOpen(false);
    const appt = appointmentData || JSON.parse(localStorage.getItem("appointmentData") || "{}");
    const appointmentId = appt?.appointmentId;

    if (appointmentId && isInstantAppointment(appt)) {
      appointmentCancel({ appointmentId, closeReason: "no_show" });
    }
    
    const finalConsultantId = appointmentData?.consultantId || appointmentData?.callerData?.consultant?.id || appointmentData?.consultant?.id || appointmentData?.consultant?._id || "";
    socket &&
      socket.emit("chatSessionReject", {
        disconnect: true,
        appointmentId: appointmentData?.appointmentId,
        role: userDetails?.roleCode,
        consultantId: finalConsultantId,
        userId: appointmentData?.callerData?.user?.id,
      });
    socket &&
      socket.emit("consultantBusy", {
        consultantId: finalConsultantId,
        userId: appointmentData?.callerData?.user?.id,
        isBusy: false,
      });
    localStorage.removeItem("appointmentData");
    localStorage.removeItem("joined");
  };

  useEffect(() => {
    let timeoutId;
    if (isOpen) {
      timeoutId = setTimeout(() => {
        const appt = appointmentData || {};
        const isInstant = isInstantAppointment(appt);
        if (isInstant) {
          toast.error("Consultant did not answer. The chat is cancelled and any charges have been voided.", { autoClose: 6000 });
        } else {
          toast.info("Consultant is currently unavailable. You can retry joining until the appointment end time.", { autoClose: 6000 });
        }
        handleJoinLater();
      }, 45000);
    }
    return () => clearTimeout(timeoutId);
  }, [isOpen, appointmentData]);

  useSocket("chatSessionRejectResponse", async (data) => {
    if (data?.disconnect === true) {
      if (isOpen) {
        const appt = appointmentData || {};
        const isInstant = isInstantAppointment(appt);
        if (isInstant) {
          toast.error("Consultant declined the chat.", { autoClose: 6000 });
        } else {
          toast.info("Consultant is currently unavailable. You can retry joining until the appointment end time.", { autoClose: 6000 });
        }
      }
      setIsOpen(false);
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
    }
  }, [isOpen, appointmentData]);

  const consultantPerson =
    appointmentData?.callerData?.consultant ||
    callerData?.consultant ||
    appointmentData?.consultant ||
    {};

  const consultantName =
    consultantPerson?.name ||
    `${consultantPerson?.firstName || ""} ${consultantPerson?.lastName || ""}`.trim() ||
    "your consultant";

  const consultantImage =
    (typeof consultantPerson?.image === "string" && consultantPerson.image) ||
    consultantPerson?.image?.url ||
    "";

  return (
    <Modal bodyClass={"!bg-white"} isOpen={isOpen} titleShow={false} size={"lg"}>
      <div className="flex flex-col justify-center items-center h-full p-6 ">
        <div className="flex items-center justify-center space-x-3 px-8 py-3 rounded-lg">
          <div className="flex items-center justify-center space-x-8 ">
            <div className="flex flex-col items-center justify-center">
              {consultantImage ? (
                <img
                  crossOrigin="anonymous"
                  src={consultantImage}
                  alt={consultantName}
                  className={"!w-32 !h-32 object-cover rounded-full mb-2 border p-1 bg-gray-100"}
                />
              ) : null}

              <div className="text-xl font-semibold  text-prontopsy-pink uppercase font-RobotoSlab">
                {consultantName}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center pb-3">
          <p className="text-base text-center font-RobotoSlab">
            Your <span className="text-base text-prontopsy-dodgerBlue font-bold capitalize">chat</span>{" "}
            consult with <span className="text-base font-bold text-prontopsy-pink">{consultantName}</span> is about to
            begin. Please hold on while they connect to the chat.
          </p>
        </div>

        <div className="flex space-x-6 text-xl">
          <button
            onClick={handleJoinLater}
            className="bg-red-500 flex gap-4 items-center  text-white px-12 py-4 rounded-lg shadow-md hover:bg-red-600 transition duration-300 ease-in-out">
            <i className="fa-solid fa-xmark"></i>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};
